import { memo, useCallback } from 'react';

import { Copy, Key, Trash2 } from 'lucide-react';
import useSWRMutation from 'swr/mutation';

import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import css from './MasterKeyCard.module.css';

import { masterKeyService } from 'v2/api/masterKeyService';
import { Card } from 'v2/components/card/Card';
import { UserRolesFormControl } from 'v2/components/form/userRolesFormControl/UserRolesFormControl';
import { Popconfirm } from 'v2/components/popconfirm/Popconfirm';
import { UserRole } from 'v2/constants';
import { MasterKeyModel } from 'v2/types/MasterKeyModel';
import { copyText, handleHttpError } from 'v2/utils';

interface MasterKeyCardProps {
  masterKey: MasterKeyModel;
  onRevalidate: VoidFunction;
}

export const MasterKeyCard = memo<MasterKeyCardProps>(({ masterKey, onRevalidate }) => {
  const { trigger: triggerDelete } = useSWRMutation(
    ['deleteMasterKey', masterKey.id],
    masterKeyService.deleteMasterKey,
    { onSuccess: () => onRevalidate(), onError: handleHttpError }
  );

  const { trigger: triggerUpdate } = useSWRMutation(
    ['updateMasterKey', masterKey.id],
    masterKeyService.updateMasterKey,
    { onSuccess: () => onRevalidate(), onError: handleHttpError }
  );

  const handleUpdateRoles = useCallback(
    (roles: UserRole[]) => {
      triggerUpdate({ name: masterKey.name, roles });
    },
    [masterKey, triggerUpdate]
  );

  const handleDeleteRole = useCallback(
    (role: UserRole) => {
      triggerUpdate({ name: masterKey.name, roles: masterKey.roles.filter((r) => r !== role) });
    },
    [masterKey, triggerUpdate]
  );

  return (
    <Card className={css.masterKeyCardContainer}>
      <div className={css.masterKeyCard}>
        <Key />

        <div className={css.masterKeyCardContent}>
          <p className={css.masterKeyCardName}>{masterKey.name}</p>

          <UserRolesFormControl roles={masterKey.roles} onUpdate={handleUpdateRoles} onDelete={handleDeleteRole} />
        </div>
      </div>

      <div className={css.masterKeyCardActions}>
        <Tooltip title="Copy" placement="top">
          <IconButton onClick={() => copyText(masterKey.key)}>
            <Copy width={22} height={22} />
          </IconButton>
        </Tooltip>

        <Popconfirm message="Are you sure you want to delete this key?" onConfirm={triggerDelete}>
          <Tooltip title="Delete" placement="top">
            <IconButton color="error">
              <Trash2 width={22} height={22} />
            </IconButton>
          </Tooltip>
        </Popconfirm>
      </div>
    </Card>
  );
});

MasterKeyCard.displayName = 'MasterKeyCard';
